import {
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import type {
  ColDef,
  DefaultMenuItem,
  FirstDataRenderedEvent,
  GetContextMenuItemsParams,
  GridApi,
  GridReadyEvent,
  GridSizeChangedEvent,
  MenuItemDef,
  ProcessCellForExportParams
} from 'ag-grid-community';
import type { AgGridReactProps } from 'ag-grid-react';
import AppContext from '@/context/Context';
import AgGridLoading from './AgGridLoading';
import AG_GRID_LOCALE_FA from '@/locales/AG_GRID_LOCALE_FA';
import { processCellForClipboard } from './configs';
import downloadxls from '@/helpers/downloadxls';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';
import AgGridHeader from './AgGridHeader';
import AgGridGroupHeader from './AgGridGroupHeader';
import type {
  AgGridHeight,
  AgGridTreeHandle,
  AgGridTreeProps,
  AgGridTreeRow
} from './types';

const transformDataForExcel = (data: AgGridTreeRow[]): (string | null)[][] => {
  const result: (string | null)[][] = [];
  const levelTracker: string[] = [];
  data.forEach(row => {
    const decodedRow = row.name.map(cell => decodeURIComponent(cell));
    decodedRow.forEach((cell, level) => {
      if (levelTracker[level] !== cell) {
        const newRow = Array(level).fill(null);
        newRow.push(cell);
        result.push(newRow);
        levelTracker[level] = cell;
        levelTracker.splice(level + 1);
      }
    });
  });

  return result;
};
const AgGridTree = forwardRef<AgGridTreeHandle, AgGridTreeProps>(
  function AgGridTree(
  {
    data,
    height,
    columnDefs = [],
    autoGroupColumnDef: autoGroupColumnDefOverride,
    autoSizeOnReady = true,
    fitColumns = false
  },
  ref
) {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const [heightState, setHeightState] = useState<AgGridHeight | undefined>(
    height
  );
  const viewRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<AgGridReact<AgGridTreeRow> | null>(null);
  const heightStateBackUp = useRef<AgGridHeight | undefined>(undefined);
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setHeightState(heightStateBackUp.current);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);
  const {
    config: { isRTL, theme }
  } = useContext(AppContext);

  const fitGridColumns = useCallback((api: GridApi<AgGridTreeRow>) => {
    if (!api || api.isDestroyed?.()) return;
    api.sizeColumnsToFit();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      expandAll: () => {
        const api = gridRef.current?.api;
        if (!api || api.isDestroyed?.()) return;
        api.expandAll();
      },
      collapseAll: () => {
        const api = gridRef.current?.api;
        if (!api || api.isDestroyed?.()) return;
        api.collapseAll();
      }
    }),
    []
  );

  const onGridReady = ({ api }: GridReadyEvent<AgGridTreeRow>) => {
    if (fitColumns) {
      fitGridColumns(api);
    } else if (autoSizeOnReady) {
      api.autoSizeColumns(
        api.getAllDisplayedColumns().map(col => col.getColId())
      );
    }
    api.expandAll();
  };

  const onGridSizeChanged = useCallback(
    ({ api }: GridSizeChangedEvent<AgGridTreeRow>) => {
      if (fitColumns) fitGridColumns(api);
    },
    [fitColumns, fitGridColumns]
  );

  const onFirstDataRendered = useCallback(
    ({ api }: FirstDataRenderedEvent<AgGridTreeRow>) => {
      if (fitColumns) fitGridColumns(api);
    },
    [fitColumns, fitGridColumns]
  );

  const autoGroupColumnDef = useMemo<ColDef<AgGridTreeRow>>(
    () => ({
      headerName: 'کلمه',
      cellRendererParams: { suppressCount: true },
      cellStyle: params => {
        const level = params.node?.level ?? 0;
        return {
          paddingInlineStart: `${level * 18}px`
        };
      },
      ...(autoGroupColumnDefOverride || { flex: 1 })
    }),
    [autoGroupColumnDefOverride]
  );
  /* AG Grid Loading  */
  const loadingCellRenderer = useMemo(() => AgGridLoading, []);
  const loadingCellRendererParams = useMemo(
    () => ({ loadingMessage: ' درحال بارگذاری اطلاعات...' }),
    []
  );

  /* حذف قابلیت های غیرضروری جدول */
  const defaultColDef = useMemo<ColDef<AgGridTreeRow>>(() => {
    return {
      // DECODER
      valueFormatter: p => {
        if (p?.value) {
          try {
            return decodeURIComponent(p.value as string);
          } catch (error) {
            console.log('Error in decode URI URLRenderer.');
            return p.value;
          }
        } else return '';
      },
      filter: false,
      sortingOrder: ['desc', 'asc', null],
      suppressHeaderMenuButton: false,
      // floatingFilterComponentParams: { suppressFilterButton: true },
      floatingFilter: false,
      resizable: true,
      sortable: true
    };
  }, []);
  /* فیلتر کردن و شخصی سازی کلیک راست جدول */
  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<AgGridTreeRow>) => {
    if (params.defaultItems) {
      const defaultMenuItems = params.defaultItems.slice();
      const filteredItems = defaultMenuItems.filter(function (item) {
        return (
          item !== 'export' &&
          item !== 'copyWithGroupHeaders' &&
          item !== 'cut' &&
          item !== 'paste' &&
          item !== 'separator'
        );
      });

      const finalResult: (DefaultMenuItem | MenuItemDef<AgGridTreeRow>)[] = [
        ...filteredItems,
        {
          name: 'تمام صفحه',
          icon: '<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="expand-arrows-alt" class="svg-inline--fa fa-expand-arrows-alt fa-w-14 fs--1" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M448 344v112a23.94 23.94 0 0 1-24 24H312c-21.39 0-32.09-25.9-17-41l36.2-36.2L224 295.6 116.77 402.9 153 439c15.09 15.1 4.39 41-17 41H24a23.94 23.94 0 0 1-24-24V344c0-21.4 25.89-32.1 41-17l36.19 36.2L184.46 256 77.18 148.7 41 185c-15.1 15.1-41 4.4-41-17V56a23.94 23.94 0 0 1 24-24h112c21.39 0 32.09 25.9 17 41l-36.2 36.2L224 216.4l107.23-107.3L295 73c-15.09-15.1-4.39-41 17-41h112a23.94 23.94 0 0 1 24 24v112c0 21.4-25.89 32.1-41 17l-36.19-36.2L263.54 256l107.28 107.3L407 327.1c15.1-15.2 41-4.5 41 16.9z"></path></svg>',
          action: () => {
            if (!document.fullscreenElement) {
              heightStateBackUp.current = heightState;
              setHeightState('100vh');
              viewRef.current?.requestFullscreen();
            } else {
              document.exitFullscreen();
            }
          }
        },
        'separator',
        {
          name: 'خروجی گرفتن',
          icon: '<span class="ag-icon ag-icon-save" unselectable="on" role="presentation"></span>',
          subMenu: [
            'csvExport',
            {
              icon: '<span class="ag-icon ag-icon-excel" unselectable="on" role="presentation"></span>',
              name: 'خروجی Excel',
              action: () => {
                downloadxls(
                  transformDataForExcel(data) as unknown as Record<
                    string,
                    unknown
                  >[]
                );
              }
            }
          ]
        }
      ];

      return finalResult;
    }
    return [];
    },
    [data, heightState]
  );

  /* AG Grid props */
  const defaultAgProps: AgGridReactProps<AgGridTreeRow> = {
    suppressRowDrag: !isMdUp,
    suppressDragLeaveHidesColumns: !isMdUp,
    suppressMovableColumns: !isMdUp,

    processCellForClipboard: (params: ProcessCellForExportParams<AgGridTreeRow>) =>
      processCellForClipboard(params, location.pathname),
    defaultExcelExportParams: {
      sheetName: 'export-from-mizfa-tools',
      author: 'mizfa-tools',
      fileName: 'export-from-mizfa-tools',
      // getCustomContentBelowRow: params => getRows(params),
      processCellCallback(params) {
        return decodeURIComponent(params.value);
      }
    },
    defaultCsvExportParams: {
      fileName: 'export-from-mizfa-tools',
      suppressQuotes: true,
      processCellCallback(params) {
        return (
          ','.repeat(params.node?.level ?? 0) +
          decodeURIComponent(params.value)
        );
      }
    },
    loadingCellRenderer: loadingCellRenderer,
    loadingCellRendererParams,
    domLayout: height === 'auto' ? 'autoHeight' : undefined,
    cellSelection: true,
    getContextMenuItems: getContextMenuItems,
    tooltipShowDelay: 0,
    tooltipHideDelay: 5000,
    columnDefs: columnDefs,
    className: 'rounded-3',
    enableRtl: isRTL,
    localeText: AG_GRID_LOCALE_FA,
    defaultColDef: defaultColDef,
    onGridReady: onGridReady,
    onGridSizeChanged,
    onFirstDataRendered,
    animateRows: true,
    suppressRowTransform: true,
    getDataPath: (rowData: AgGridTreeRow) => rowData.name,
    treeData: true,
    paginationAutoPageSize: false,
    paginationPageSizeSelector: false
  };

  /* AG Grid Components */
  const components = useMemo(
    () => ({
      agColumnHeader: AgGridHeader,
      agColumnGroupHeader: AgGridGroupHeader
    }),
    []
  );
  return (
    <div
      ref={viewRef}
      className={`overflow-hidden ${
        theme === 'dark' ? 'ag-theme-balham-dark' : 'ag-theme-balham'
      } ag-theme-custom`}
      style={{
        height:
          defaultAgProps.domLayout === 'autoHeight' ? undefined : heightState,
        width: '100%'
      }}
    >
      <AgGridReact<AgGridTreeRow>
        rowData={data ?? []}
        ref={gridRef}
        autoGroupColumnDef={autoGroupColumnDef}
        components={components}
        {...defaultAgProps}
      />
    </div>
  );
});

export default memo(AgGridTree);
